"use client";

import { ArrowRight, X } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/common/Button";
import { GridIcon } from "@/components/icons/GridIcon";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

// Products configuration for dropdown
const productsData = [
  {
    id: "loyalty",
    name: "Loyalty & Referrals",
    description: "To build the reliable tools that power merchant growth.",
    icon: "/brands/loyalty-square.svg",
  },
  {
    id: "product-options",
    name: "Product Options",
    description: "Create limitless product choices and variants",
    icon: "/brands/product-options-square.svg",
  },
  {
    id: "ai-seo",
    name: "AI SEO Optimize for LLMs",
    description: "Get your Shopify store discovered by AI",
    icon: "/brands/ai-seo-square.svg",
  },
];

// Contact options for dropdown
const contactOptions = [
  {
    id: "hello",
    description: "Custom quotes and enterprise solutions",
    email: "hello@key-stone.app",
  },
  {
    id: "marketing",
    description: "Marketing and brand partnerships",
    email: "marketing@key-stone.app",
  },
  {
    id: "support",
    description: "24/7 technical assistance and troubleshooting",
    email: "support@key-stone.app",
  },
];

// All resource tabs for dropdown (filtered by feature flags at render)
const allResourceTabs = [
  { id: "blog", label: "Blog", href: "/blogs", flagKey: "blogsEnabled" },
  {
    id: "guides",
    label: "Guides",
    href: "/resources/guides",
    flagKey: "guidesEnabled",
  },
  {
    id: "case-studies",
    label: "Case Studies",
    href: "/resources/case-studies",
    flagKey: "caseStudiesEnabled",
  },
  {
    id: "ebooks",
    label: "eBooks",
    href: "/resources/ebooks",
    flagKey: "ebooksEnabled",
  },
  {
    id: "research-papers",
    label: "Research Papers",
    href: "/resources/research-papers",
    flagKey: "researchPapersEnabled",
  },
  {
    id: "changelog",
    label: "Changelog",
    href: "/resources/changelog",
    navigate: true,
  },
];

export const Header = ({ setActiveTab }) => {
  const router = useRouter();
  const { featureFlags } = useFeatureFlags();
  const resourceTabs = useMemo(
    () =>
      allResourceTabs.filter(
        (tab) => !tab.flagKey || featureFlags[tab.flagKey] !== false,
      ),
    [featureFlags],
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [activeResourceTab, setActiveResourceTab] = useState("blog");
  // Mobile menu accordion state: null | "products" | "resources" | "contact"
  const [mobileExpanded, setMobileExpanded] = useState("products");

  // Dynamic resource articles from API
  const [latestResources, setLatestResources] = useState({});
  const [resourcesFetched, setResourcesFetched] = useState(false);

  const fetchLatestResources = useCallback(async () => {
    if (resourcesFetched) return;
    try {
      const res = await fetch("/api/resources/latest");
      const json = await res.json();
      if (json.success && json.data) {
        setLatestResources(json.data);
        setResourcesFetched(true);
      }
    } catch (err) {
      console.warn("Failed to fetch latest resources:", err.message);
    }
  }, [resourcesFetched]);

  // Prefetch on mount so data is ready when dropdown opens
  useEffect(() => {
    fetchLatestResources();
  }, [fetchLatestResources]);

  // Set default active tab to first available tab
  useEffect(() => {
    if (
      resourceTabs.length > 0 &&
      !resourceTabs.find((t) => t.id === activeResourceTab)
    ) {
      setActiveResourceTab(resourceTabs[0].id);
    }
  }, [resourceTabs, activeResourceTab]);

  // Get articles for the active tab
  const activeArticles = latestResources[activeResourceTab] || [];

  const scrollToContact = useCallback(() => {
    const start = Date.now();
    const tryScroll = () => {
      const el = document.getElementById("contact");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else if (Date.now() - start < 3000) {
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  }, []);

  const goToContact = useCallback(() => {
    if (window.location.pathname === "/") {
      setActiveTab?.("homepage");
      setTimeout(scrollToContact, 50);
    } else {
      router.push("/#contact");
    }
  }, [router, setActiveTab, scrollToContact]);

  const closeAllDropdowns = () => {
    setIsBrandsOpen(false);
    setIsResourcesOpen(false);
    setIsContactOpen(false);
  };

  const toggleMobileSection = (section) => {
    setMobileExpanded((prev) => (prev === section ? null : section));
  };

  // Escape key + body overflow lock
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        closeAllDropdowns();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const isAnyDropdownOpen = isBrandsOpen || isResourcesOpen || isContactOpen;

  const navItems = [
    { label: "Products", isOpen: isBrandsOpen, setState: setIsBrandsOpen },
    {
      label: "Resources",
      isOpen: isResourcesOpen,
      setState: setIsResourcesOpen,
    },
    { label: "Contact", isOpen: isContactOpen, setState: setIsContactOpen },
  ];

  // Determine "Explore all" link based on active tab (deep link to the
  // matching category page so crawlers and users land on the right hub)
  const exploreAllHref =
    resourceTabs.find((tab) => tab.id === activeResourceTab)?.href ||
    "/resources";

  return (
    <>
      {/* ==================== HEADER ==================== */}
      <header className="fixed top-8 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-[1280px]">
        <div
          className="rounded-[24px] border border-[rgba(96,96,96,0.2)] transition-all duration-300"
          style={{
            background: isAnyDropdownOpen
              ? "rgba(30, 30, 30, 0.6)"
              : "linear-gradient(0deg, #1E1E1E 0%, #1A1A1A 100%)",
            backdropFilter: isAnyDropdownOpen
              ? "blur(40px) saturate(1.5)"
              : "none",
            WebkitBackdropFilter: isAnyDropdownOpen
              ? "blur(40px) saturate(1.5)"
              : "none",
            boxShadow:
              "9px 9px 23.4px 0px rgba(0, 0, 0, 0.25), -9px -5px 23.4px 0px rgba(200, 200, 200, 0.08)",
          }}
          onMouseLeave={() => closeAllDropdowns()}
        >
          {/* ===== Level-1: Top Bar ===== */}
          <div
            className={`flex items-center justify-between pl-6 pr-[18px] pt-4 pb-4 ${
              isAnyDropdownOpen ? "border-b border-[#2e2e2e]" : ""
            }`}
          >
            {/* Logo */}
            <Link
              href="/"
              onClick={() => setActiveTab?.("homepage")}
              className="flex items-center gap-3 shrink-0 cursor-pointer"
            >
              <Image
                src="/brands/keystone-icon.svg"
                alt="Keystone"
                width={20}
                height={17}
              />
              <Image
                src="/brands/keystone-wordmark.svg"
                alt="Keystone"
                width={94}
                height={27}
              />
            </Link>

            {/* Desktop Nav - centered */}
            <nav className="hidden lg:flex items-center gap-4 absolute left-0 right-0 justify-center pointer-events-none">
              <div className="flex items-center gap-4 pointer-events-auto">
              {navItems.map(({ label, isOpen, setState }) => (
                <button
                  key={label}
                  onMouseEnter={() => {
                    closeAllDropdowns();
                    setState(true);
                  }}
                  onClick={() => setState(!isOpen)}
                  className={`px-3 pt-3 pb-[14px] text-[14px] leading-6 cursor-pointer transition-colors rounded-b-sm ${
                    isOpen
                      ? "text-[#f8f7f4] border-b-2 border-[#bbe1e6]"
                      : "text-[#a4a4a4] hover:text-[#f8f7f4]"
                  }`}
                >
                  {label}
                </button>
              ))}
              </div>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:block">
              <Button
                variant="primary"
                href="https://apps.shopify.com/partners/key-stone?utm_source=website&utm_medium=organic&utm_campaign=header"
              >
                Try Keystone
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="primary"
              size="icon"
              onClick={() => setIsMobileMenuOpen(true)}
              className="flex lg:hidden"
              aria-label="Open mobile menu"
            >
              <GridIcon />
            </Button>
          </div>

          {/* ===== PRODUCTS DROPDOWN ===== */}
          {isBrandsOpen && (
            <div className="hidden lg:flex flex-col xl:flex-row gap-6 xl:gap-4 px-6 pt-12 pb-6">
              {/* Tagline: full width on lg, left column on xl */}
              <div className="xl:shrink-0 xl:w-[385px] xl:pt-4">
                <p className="text-[20px] xl:text-[24px] text-[#f8f7f4] leading-7 xl:leading-8 tracking-[-0.6px] xl:tracking-[-0.72px] xl:w-[288px]">
                  Grow your Shopify store with apps that actually work
                </p>
              </div>

              {/* Product cards */}
              <div className="flex flex-1 flex-wrap xl:flex-nowrap gap-4">
                {productsData.map((product) => (
                  <Link
                    key={product.id}
                    href={`/${product.id}`}
                    onClick={() => closeAllDropdowns()}
                    className="flex-1 min-w-[180px] flex flex-col gap-4 p-4 rounded-lg bg-[rgba(46,46,46,0.8)] hover:bg-[rgba(60,60,60,0.8)] transition-colors cursor-pointer text-left"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
                      <Image
                        src={product.icon}
                        alt={product.name}
                        width={40}
                        height={40}
                        className="object-contain w-full h-full"
                      />
                    </div>
                    <div className="flex flex-col gap-6 flex-1">
                      <div>
                        <p className="text-[14px] font-medium text-[#f8f7f4] leading-5 tracking-[-0.14px]">
                          {product.name}
                        </p>
                        <p className="text-[14px] text-[#a4a4a4] leading-5 tracking-[-0.028px]">
                          {product.description}
                        </p>
                      </div>
                      <span className="text-[14px] text-[#f8f7f4] leading-[14px] flex items-center gap-1 py-2">
                        Learn more <ArrowRight className="w-4 h-3" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* ===== RESOURCES DROPDOWN ===== */}
          {isResourcesOpen && (
            <div className="hidden lg:flex px-6 pt-12 pb-6">
              {/* xl: left column with title + explore all */}
              <div className="hidden xl:flex flex-col gap-6 shrink-0 w-[385px] pt-4">
                <p className="text-[24px] text-[#f8f7f4] leading-8 tracking-[-0.72px] w-[288px]">
                  All our guides and tips to grow your Shopify store in one
                  place
                </p>
                <Link
                  href={exploreAllHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => closeAllDropdowns()}
                  className="text-[14px] text-[#a4a4a4] leading-4 flex items-center gap-1 py-2 hover:text-[#f8f7f4] transition-colors"
                >
                  Explore all
                </Link>
              </div>

              {/* Right side (or full width on lg) */}
              <div className="flex flex-col gap-4 flex-1">
                {/* lg-only: title */}
                <p className="xl:hidden text-[20px] text-[#f8f7f4] leading-7 tracking-[-0.6px]">
                  All our guides and tips to grow your Shopify store in one
                  place
                </p>

                {/* Sub-nav + explore all */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {resourceTabs.map((tab) => (
                      <div key={tab.id} className="flex items-center gap-3">
                        {tab.navigate ? (
                          <Link
                            href={tab.href}
                            onClick={() => closeAllDropdowns()}
                            className={`text-[16px] leading-5 py-2 cursor-pointer transition-colors ${
                              activeResourceTab === tab.id
                                ? "text-[#f8f7f4]"
                                : "text-[#a4a4a4] hover:text-[#f8f7f4]"
                            }`}
                          >
                            {tab.label}
                          </Link>
                        ) : (
                          <button
                            onClick={() => setActiveResourceTab(tab.id)}
                            className={`text-[16px] leading-5 py-2 cursor-pointer transition-colors ${
                              activeResourceTab === tab.id
                                ? "text-[#f8f7f4]"
                                : "text-[#a4a4a4] hover:text-[#f8f7f4]"
                            }`}
                          >
                            {tab.label}
                          </button>
                        )}
                        <span className="text-[16px] text-[#a4a4a4] leading-5">
                          /
                        </span>
                      </div>
                    ))}
                    <Link
                      href="/docs"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => closeAllDropdowns()}
                      className="text-[16px] leading-5 py-2 text-[#a4a4a4] hover:text-[#f8f7f4] transition-colors"
                    >
                      Docs
                    </Link>
                  </div>
                  <Link
                    href={exploreAllHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => closeAllDropdowns()}
                    className="xl:hidden text-[16px] text-[#f8f7f4] leading-4 flex items-center gap-1 py-2 hover:opacity-80 transition-opacity"
                  >
                    Explore all <ArrowRight className="w-4 h-3" />
                  </Link>
                </div>

                {/* Article cards — always 3 slots on desktop */}
                <div className="flex gap-4">
                  {Array.from({ length: 3 }).map((_, i) => {
                    const article = activeArticles[i];
                    if (article) {
                      return (
                        <Link
                          key={article.id}
                          href={article.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => closeAllDropdowns()}
                          className="flex-1 flex flex-col h-[250px] rounded-lg bg-[rgba(46,46,46,0.6)] hover:bg-[rgba(60,60,60,0.6)] transition-colors overflow-hidden"
                        >
                          {article.image ? (
                            <div className="flex-1 relative overflow-hidden">
                              <Image
                                src={article.image}
                                alt={article.title}
                                fill
                                sizes="320px"
                                className="object-cover"
                              />
                            </div>
                          ) : (
                            <div className="flex-1 bg-[rgba(60,60,60,0.4)]" />
                          )}
                          <div className="flex flex-col gap-2 p-4">
                            <p className="text-[14px] font-medium text-[#f8f7f4] leading-5 tracking-[-0.14px] line-clamp-2">
                              {article.title}
                            </p>
                            <span className="text-[14px] text-[#f8f7f4] leading-[14px] flex items-center gap-1 py-2">
                              Learn more <ArrowRight className="w-4 h-3" />
                            </span>
                          </div>
                        </Link>
                      );
                    }
                    return (
                      <div
                        key={`placeholder-${i}`}
                        className="flex-1 h-[250px] rounded-lg bg-[rgba(46,46,46,0.3)]"
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ===== CONTACT DROPDOWN ===== */}
          {isContactOpen && (
            <div className="hidden lg:flex flex-col xl:flex-row gap-6 xl:gap-4 px-6 pt-12 pb-6">
              {/* Tagline: full width on lg, left column on xl */}
              <div className="xl:shrink-0 xl:w-[385px] xl:pt-4 flex flex-col gap-6">
                <p className="text-[20px] xl:text-[24px] text-[#f8f7f4] leading-7 xl:leading-8 tracking-[-0.6px] xl:tracking-[-0.72px] xl:w-[288px]">
                  Ready to transform your{" "}
                  <span className="whitespace-nowrap">e-commerce</span>{" "}
                  business?
                </p>
                <Link
                  href="/#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    closeAllDropdowns();
                    goToContact();
                  }}
                  className="text-[14px] text-[#a4a4a4] leading-4 flex items-center gap-1 py-2 hover:text-[#f8f7f4] transition-colors"
                >
                  Contact us <ArrowRight className="w-4 h-3" />
                </Link>
              </div>

              {/* Contact cards */}
              <div className="flex flex-1 flex-wrap xl:flex-nowrap gap-4">
                {contactOptions.map((option) => (
                  <a
                    key={option.id}
                    href={`mailto:${option.email}`}
                    className="flex-1 min-w-[180px] flex flex-col gap-2 p-4 rounded-lg bg-[rgba(46,46,46,0.6)] hover:bg-[rgba(60,60,60,0.6)] transition-colors"
                  >
                    <p className="text-[14px] font-medium text-[#f8f7f4] leading-5 tracking-[-0.14px]">
                      {option.description}
                    </p>
                    <p className="text-[14px] text-[#a4a4a4] leading-4">
                      {option.email}
                    </p>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ==================== MOBILE MENU ==================== */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden flex flex-col"
          style={{
            background: "rgba(30, 30, 30, 0.6)",
            backdropFilter: "blur(23px)",
            WebkitBackdropFilter: "blur(23px)",
          }}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between pl-6 pr-[18px] pt-4 pb-4 border-b border-[#2e2e2e]">
            <Link
              href="/"
              onClick={() => {
                setActiveTab?.("homepage");
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3"
            >
              <Image
                src="/brands/keystone-icon.svg"
                alt="Keystone"
                width={20}
                height={17}
              />
              <Image
                src="/brands/keystone-wordmark.svg"
                alt="Keystone"
                width={94}
                height={27}
              />
            </Link>
            <Button
              variant="primary"
              size="icon"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close mobile menu"
            >
              <X className="w-5 h-5 text-[#888888]" />
            </Button>
          </div>

          {/* Accordion menu items */}
          <div className="flex-1 flex flex-col px-6 overflow-y-auto">
            <div className="flex flex-col gap-4">
              {/* ===== Products Section ===== */}
              <button
                onClick={() => toggleMobileSection("products")}
                className={`border-b border-[#606060] flex flex-col gap-4 items-start pb-6 pt-3 text-left cursor-pointer transition-opacity ${
                  mobileExpanded && mobileExpanded !== "products"
                    ? "opacity-50"
                    : ""
                }`}
              >
                <span className="text-[20px] text-[#f8f7f4] leading-8">
                  Products
                </span>

                {/* Products expanded content */}
                {mobileExpanded === "products" && (
                  <div className="flex flex-col gap-4 w-full">
                    {productsData.map((product) => (
                      <Link
                        key={product.id}
                        href={`/${product.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex flex-col gap-4 p-4 rounded-lg bg-[rgba(46,46,46,0.8)] cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
                          <Image
                            src={product.icon}
                            alt={product.name}
                            width={40}
                            height={40}
                            className="object-contain w-full h-full"
                          />
                        </div>
                        <div className="flex flex-col gap-6">
                          <div>
                            <p className="text-[14px] font-medium text-[#f8f7f4] leading-5 tracking-[-0.14px]">
                              {product.name}
                            </p>
                            <p className="text-[14px] text-[#a4a4a4] leading-5 tracking-[-0.028px]">
                              {product.description}
                            </p>
                          </div>
                          <span className="text-[14px] text-[#f8f7f4] leading-[14px] flex items-center gap-1 py-2">
                            Learn more <ArrowRight className="w-4 h-3" />
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </button>

              {/* ===== Resources Section ===== */}
              <button
                onClick={() => toggleMobileSection("resources")}
                className={`border-b border-[#606060] flex flex-col gap-4 items-start pb-6 pt-3 text-left cursor-pointer transition-opacity ${
                  mobileExpanded && mobileExpanded !== "resources"
                    ? "opacity-50"
                    : ""
                }`}
              >
                <span className="text-[20px] text-[#f8f7f4] leading-8">
                  Resources
                </span>

                {/* Resources description (always shown) */}
                <div className="py-6 w-full">
                  <p className="text-[16px] text-[#a4a4a4] leading-6 tracking-[-0.48px]">
                    All our guides and tips to grow your Shopify store in one
                    place
                  </p>
                </div>

                {/* Resources sub-tabs (always shown) */}
                <div className="flex items-center gap-3 flex-wrap">
                  {resourceTabs.map((tab, index) => (
                    <div key={tab.id} className="flex items-center gap-3">
                      {tab.navigate ? (
                        <Link
                          href={tab.href}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsMobileMenuOpen(false);
                          }}
                          className={`text-[16px] leading-5 py-2 cursor-pointer ${
                            activeResourceTab === tab.id
                              ? "text-[#f8f7f4]"
                              : "text-[#a4a4a4]"
                          }`}
                        >
                          {tab.label}
                        </Link>
                      ) : (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveResourceTab(tab.id);
                          }}
                          className={`text-[16px] leading-5 py-2 cursor-pointer ${
                            activeResourceTab === tab.id
                              ? "text-[#f8f7f4]"
                              : "text-[#a4a4a4]"
                          }`}
                        >
                          {tab.label}
                        </span>
                      )}
                      {index < resourceTabs.length - 1 && (
                        <span className="text-[16px] text-[#a4a4a4] leading-5">
                          /
                        </span>
                      )}
                    </div>
                  ))}
                  <span className="text-[16px] text-[#a4a4a4] leading-5">
                    /
                  </span>
                  <Link
                    href="/docs"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-[16px] leading-5 py-2 text-[#a4a4a4]"
                  >
                    Docs
                  </Link>
                </div>

                {/* Resources expanded: dynamic article cards */}
                {mobileExpanded === "resources" && (
                  <div
                    className="flex flex-col gap-4 w-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {activeArticles.length > 0 ? (
                      activeArticles.slice(0, 2).map((article) => (
                        <Link
                          key={article.id}
                          href={article.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex flex-col h-[250px] rounded-lg bg-[rgba(46,46,46,0.6)] overflow-hidden"
                        >
                          {article.image ? (
                            <div className="flex-1 relative overflow-hidden">
                              <Image
                                src={article.image}
                                alt={article.title}
                                fill
                                sizes="320px"
                                className="object-cover"
                              />
                            </div>
                          ) : (
                            <div className="flex-1 bg-[rgba(60,60,60,0.4)]" />
                          )}
                          <div className="flex flex-col gap-2 p-4">
                            <p className="text-[14px] font-medium text-[#f8f7f4] leading-5 tracking-[-0.14px] line-clamp-2">
                              {article.title}
                            </p>
                            <span className="text-[14px] text-[#a4a4a4] leading-4">
                              Read full article
                            </span>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="flex items-center justify-center h-[120px] rounded-lg bg-[rgba(46,46,46,0.3)]">
                        <p className="text-[14px] text-[#909090]">
                          No content yet — check back soon
                        </p>
                      </div>
                    )}
                    <Link
                      href={exploreAllHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center justify-end gap-2 py-4 text-[14px] text-[#f8f7f4] leading-4"
                    >
                      Explore all resources <ArrowRight className="w-3.5 h-3" />
                    </Link>
                  </div>
                )}
              </button>

              {/* ===== Contact Section ===== */}
              <button
                onClick={() => toggleMobileSection("contact")}
                className={`border-b border-[#606060] flex flex-col gap-4 items-start pb-6 pt-3 text-left cursor-pointer transition-opacity ${
                  mobileExpanded && mobileExpanded !== "contact"
                    ? "opacity-50"
                    : ""
                }`}
              >
                <span className="text-[20px] text-[#f8f7f4] leading-8">
                  Contact
                </span>

                {/* Contact expanded content */}
                {mobileExpanded === "contact" && (
                  <div
                    className="flex flex-col gap-4 w-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Description + CTA */}
                    <div className="flex flex-col gap-3 py-6">
                      <p className="text-[16px] text-[#a4a4a4] leading-6 tracking-[-0.48px]">
                        Ready to transform your{" "}
                        <span className="whitespace-nowrap">e-commerce</span>{" "}
                        business?
                      </p>
                      <Link
                        href="/#contact"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsMobileMenuOpen(false);
                          goToContact();
                        }}
                        className="text-[16px] text-[#f8f7f4] leading-4 flex items-center gap-1 py-2 w-fit"
                      >
                        Contact us <ArrowRight className="w-4 h-3" />
                      </Link>
                    </div>

                    {/* Contact cards */}
                    {contactOptions.map((option) => (
                      <a
                        key={option.id}
                        href={`mailto:${option.email}`}
                        className="flex flex-col gap-2 p-4 rounded-lg bg-[rgba(46,46,46,0.6)]"
                      >
                        <p className="text-[14px] font-medium text-[#f8f7f4] leading-5 tracking-[-0.14px]">
                          {option.description}
                        </p>
                        <div className="flex items-center gap-2 py-4">
                          <span className="text-[14px] text-[#a4a4a4] leading-4">
                            {option.email}
                          </span>
                          <ArrowRight className="w-3.5 h-3 text-[#a4a4a4]" />
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Bottom CTA - dark style per Figma */}
          <div className="px-6 pb-6 pt-6">
            <Button
              variant="primary"
              href="https://apps.shopify.com/partners/key-stone?utm_source=website&utm_medium=organic&utm_campaign=header"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Try Keystone
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
